import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { createPortalLink, listInvoices } from '@/lib/firebase-functions';

interface BillingModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

// Componente para mostrar la lista de facturas
const InvoiceHistory = () => {
  const { t } = useTranslation();
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['invoices'],
    queryFn: async ({ pageParam }) => {
      const result = await listInvoices({ starting_after: pageParam });
      return result.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore && lastPage.invoices.length > 0) {
        return lastPage.invoices[lastPage.invoices.length - 1].id;
      }
      return undefined;
    },
  });

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollableViewport = scrollAreaRef.current?.querySelector(':scope > [data-radix-scroll-area-viewport]');

    if (!scrollableViewport) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { 
        root: scrollableViewport,
        threshold: 0.1 
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) return <p className='p-4 text-center'>{t('billing.loadingInvoices')}</p>;
  if (error) return <p className="p-4 text-center text-destructive">{t('billing.errorInvoices')}</p>;

  const allInvoices = data?.pages.flatMap(page => page.invoices) ?? [];

  if (allInvoices.length === 0) return <p className='p-4 text-center'>{t('billing.noInvoices')}</p>;

  return (
    <Card className="w-full">
      
      <CardContent>
        <ScrollArea className="h-72" ref={scrollAreaRef}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('billing.date')}</TableHead>
                <TableHead>{t('billing.status')}</TableHead>
                <TableHead className="text-right">{t('billing.total')}</TableHead>
                <TableHead className="text-right">{t('billing.invoice')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allInvoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>{new Date(invoice.createdAt * 1000).toLocaleDateString()}</TableCell>
                  <TableCell className="capitalize">{invoice.status}</TableCell>
                  <TableCell className="text-right">${invoice.amount.toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    <Button asChild variant="outline" size="sm" disabled={!invoice.pdfUrl}>
                      <a href={invoice.pdfUrl || '#'} target="_blank" rel="noopener noreferrer">{t('billing.download')}</a>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div ref={loadMoreRef} />
        </ScrollArea>
        {isFetchingNextPage && <p className="pt-4 text-center">{t('billing.loadingMore')}</p>}
      </CardContent>
    </Card>
  );
}

export function BillingModal({ isOpen, onOpenChange }: BillingModalProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleManageSubscription = async () => {
    setIsLoading(true);
    try {
      const result = await createPortalLink({ returnUrl: window.location.href });
      window.open(result.data.url, '_blank');
    } catch (error: any) {
      toast.error(error.message || t('billing.portalError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('header.billing')}</DialogTitle>
          <DialogDescription>{t('billing.description')}</DialogDescription>
        </DialogHeader>

        {/* Historial de Facturas */}
        <div className="py-4">
          {isOpen && <InvoiceHistory />}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between sm:space-x-2 pt-4">
            <Button 
              onClick={handleManageSubscription}
              disabled={isLoading}
            >
              {isLoading ? t('billing.loading') : t('billing.subscriptions')}
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.close')}
            </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}